import { getStoreDefinition } from '@/composables/StoreDefinition'
import { getEarliestDateTime, getLatestDateTime } from '@/composables/utils'
import { DatabaseView } from '@/enums/DatabaseView'
import { EndPoint } from '@/enums/EndPoint'
import { EventCategory } from '@/enums/EventCategory'
import { EventStatus } from '@/enums/EventStatus'
import { SearchType } from '@/enums/SearchType'
import { SqlComparison } from '@/enums/SqlComparison'
import { SqlOperator } from '@/enums/SqlOperator'
import { SqlOrder } from '@/enums/SqlOrder'
import { StoredProcedure } from '@/enums/StoredProcedure'
import { TableStoreOptions } from '@/enums/TableStoreOptions'
import type { IEventsStore } from '@/interfaces/api/IEventsStore'
import type { IQueryOptions } from '@/interfaces/api/IQueryOptions'
import type { ITableStore } from '@/interfaces/api/ITableStore'
import type { ICalendarRange } from '@/interfaces/ICalendarRange'
import type { ISimpleOption } from '@/interfaces/ISimpleOption'
import type { EventRecord } from '@/models/EventRecord'
import type { TableRecord } from '@/models/TableRecord'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useSettingsStore } from '../ui/SettingsStore'
import { useAPIStore } from './ApiStore'

let _store: IEventsStore

export const useEventsStore = (): ITableStore => {
  if (_store) {
    return _store
  }
  const option = TableStoreOptions.Events
  const useStore = defineStore(option.id, getStoreDefinition(option), {
    persist: {
      storage: localStorage,
      pick: ['itemsPerPage']
    }
  })
  _store = useStore() as unknown as IEventsStore
  _extend(_store)
  return _store
}

const _extend = (store: IEventsStore) => {
  const apiStore = useAPIStore()
  const settingsStore = useSettingsStore()
  const i18n = useI18n({ useScope: 'global' })

  store.DEFAULT_DAY_RANGE = 4
  store.MAX_DATE = 730

  store.useView(DatabaseView.EventWithDetail)
  store.itemsPerPage = 5
  store.searchType = SearchType.Query

  store.clearOpenSlots = async (
    startDate: Date,
    endDate: Date,
    daysSelected: Array<number>,
    eventCategory: EventCategory
  ): Promise<void> => {
    const daysSelectedValue = daysSelected.join(',')
    await apiStore.execute(StoredProcedure.DeleteOpenSlots, [
      startDate.toISOString(),
      endDate.toISOString(),
      daysSelectedValue,
      eventCategory
    ])
  }

  store.getCategoryOptions = (includeAll: boolean = false): Array<ISimpleOption> => {
    const categories = [
      { title: i18n.t('labels.categories.testing'), value: EventCategory.FitTest }
    ]
    if (settingsStore.globals.clearance.enabled) {
      categories.push({
        title: i18n.t('labels.categories.clearance'),
        value: EventCategory.MedicalClearance
      })
    }
    if (settingsStore.globals.training.enabled) {
      categories.push({
        title: i18n.t('labels.categories.training'),
        value: EventCategory.Training
      })
    }
    if (includeAll) {
      categories.unshift({
        title: i18n.t('labels.categories.all'),
        value: EventCategory.All
      })
    }
    return categories
  }

  /**
   * Get the event title based on the event category.
   * @param event An event instance.
   * @returns
   */
  store.getCategoryTitle = (category?: EventCategory, status?: EventStatus): string => {
    let title = ''
    switch (category) {
      case EventCategory.FitTest: {
        title = i18n.t('labels.categories.testing')
        break
      }
      case EventCategory.MedicalClearance: {
        title = i18n.t('labels.categories.clearance')
        break
      }
      case EventCategory.Training: {
        title = i18n.t('labels.categories.training')
        break
      }
    }
    if (status === EventStatus.Tentative) {
      title += ` (${i18n.t('labels.categories.openSlot')})`
    }
    return title
  }

  store.getEventConflictsByDeviceId = async (
    deviceId: number,
    startDate: Date,
    endDate: Date,
    eventId?: number
  ): Promise<Array<EventRecord>> => {
    const newStartDate = dayjs(startDate).add(1, 'second').toISOString()
    const newEndDate = dayjs(endDate).subtract(1, 'second').toISOString()
    const query: IQueryOptions = {
      isDistinct: true,
      fields: ['id', 'dtStart', 'dtEnd'],
      orderBy: [{ field: 'dtStart', order: SqlOrder.ASC }],
      where: [
        {
          comparison: SqlComparison.Equal,
          field: 'deviceId',
          value1: deviceId,
          operator: SqlOperator.And,
          children: [
            {
              comparison: SqlComparison.Between,
              field: 'dtStart',
              value1: newStartDate,
              value2: newEndDate,
              operator: SqlOperator.Or
            },
            {
              comparison: SqlComparison.Between,
              field: 'dtEnd',
              value1: newStartDate,
              value2: newEndDate
            }
          ]
        }
      ]
    }

    if (eventId) {
      query.where?.unshift({
        comparison: SqlComparison.NotEqual,
        field: 'id',
        value1: eventId,
        operator: SqlOperator.And
      })
    }

    const response = await apiStore.getMany(EndPoint.Events, query)
    let items: Array<TableRecord> = []
    if (!response.error && response.data.results) {
      items = response.data?.results
    }
    return items as Array<EventRecord>
  }

  store.getLocationValues = async (): Promise<Array<string>> => {
    const query: IQueryOptions = {
      isDistinct: true,
      fields: ['location'],
      orderBy: [{ field: 'location', order: SqlOrder.ASC }]
    }
    const response = await apiStore.getMany(EndPoint.Events, query)
    let values: Array<string> = []
    if (!response.error) {
      const items = response.data?.results || []
      values = items.map((item) => {
        return item.location
      })
    }
    return values
  }

  store.getMinStartDate = (): string => {
    return getEarliestDateTime().toISOString()
  }

  store.getMaxStartDate = (endDate: Date) => {
    if (!dayjs(endDate).isValid()) {
      endDate = new Date()
    }
    return getLatestDateTime(endDate).toISOString()
  }

  store.getMinEndDate = (startDate: Date) => {
    if (!dayjs(startDate).isValid()) {
      startDate = new Date()
    }
    return getEarliestDateTime(startDate).toISOString()
  }

  store.getMaxEndDate = (endDate: Date) => {
    if (!dayjs(endDate).isValid()) {
      endDate = new Date()
    }
    const day = dayjs(endDate).add(store.MAX_DATE, 'day').toDate()
    return getLatestDateTime(day).toISOString()
  }

  store.loadEventsByRange = async (range: ICalendarRange) => {
    store.items = []
    store.itemQuery = {
      where: [
        {
          field: 'dtStart',
          comparison: SqlComparison.Between,
          value1: new Date(range.start).toISOString(),
          value2: new Date(range.end).toISOString()
        }
      ],
      orderBy: [
        { field: 'dtStart', order: SqlOrder.ASC },
        { field: 'category', order: SqlOrder.ASC }
      ]
    }
    await store.loadItems()
  }

  store.setEventsFilterForAttendee = (id: number, dtStart: string) => {
    store.items = []
    store.count = 0
    store.itemQuery = {
      limit: store.itemsPerPage,
      where: [
        {
          comparison: SqlComparison.GreaterThanOrEqual,
          field: 'dtStart',
          value1: dtStart,
          operator: SqlOperator.And
        },
        {
          comparison: SqlComparison.Equal,
          field: 'attendeeId',
          value1: id
        }
      ],
      orderBy: [{ field: 'dtStart', order: SqlOrder.DESC }]
    }
  }

  store.setEventsFilterForOrganizer = (id: number, dtStart: string) => {
    store.items = []
    store.count = 0
    store.itemQuery = {
      limit: store.itemsPerPage,
      where: [
        {
          comparison: SqlComparison.GreaterThanOrEqual,
          field: 'dtStart',
          value1: dtStart,
          operator: SqlOperator.And
        },
        {
          comparison: SqlComparison.Equal,
          field: 'organizerId',
          value1: id
        }
      ],
      orderBy: [{ field: 'dtStart', order: SqlOrder.DESC }]
    }
  }
}
